import { t } from "@/i18n-js/instance";
import { type PlanTiers } from "@/react/helpers/planHelpers";

export interface TierHighlight {
  color?: string;
  fontWeight?: string;
  highlightColor?: string;
  highlightIcon?: string;
  text: string;
}

const PLUS_HIGHLIGHT_COLOR = "text-slate-700 dark:text-slate-100";

export const getTierHighlights = (): Record<PlanTiers, TierHighlight[]> => ({
  basic: [
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.rich_member_profiles",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.search_member_profiles",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.discussions",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.events",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.paid_memberships",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.weekly_digest",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.gamification",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.basic.custom_domain",
      ),
    },
  ],
  professional: [
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.circle_ai",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.courses",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.website_builder",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.live_events",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.unlimited_members",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.custom_branding",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.reporting_and_analytics",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.professional.custom_snippets_and_tracking",
      ),
    },
  ],
  business: [
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.business.workflows",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.business.custom_profile_fields",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.business.headless_member_api",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.business.admin_api",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.business.branded_email_notifications",
      ),
    },
  ],
  enterprise: [
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.ai_agents",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.ai_workflows",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.unlimited_workflows",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.custom_sso",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.higher_limits",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.priority_support",
      ),
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.enterprise.concierge_onboarding",
      ),
    },
  ],
  plus: [
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.custom_branded_mobile_apps",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.in_app_purchases",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.unlimited_admins_moderators_spaces",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.email_hub",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.negotiable_limits",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.csm",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.priority_technical_support",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.priority_platform_migrations",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
    {
      text: t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.tier_highlights.plus.concierge_onboarding",
      ),
      highlightColor: PLUS_HIGHLIGHT_COLOR,
    },
  ],
});
